'use client';

import { type FC, type MouseEvent, useCallback, useMemo, useRef } from 'react';

import clsx from 'clsx';

import linkStyles from '@/components/common/Link/Link.module.scss';
import { Text, type TextProps } from '@/components/common/Text';
import { Tooltip, type TooltipProps } from '@/components/common/Tooltip';
import { useCombineRefs } from '@/hooks/use-combine-refs';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useElementSize } from '@/hooks/use-element-size';
import { useIsTouch } from '@/hooks/use-is-touch';
import { getNodeText } from '@/utils/react';

import styles from './OverflowingTextTip.module.scss';

export type OverflowingTextTipProps = Pick<
  TextProps,
  'className' | 'type' | 'color' | 'align' | 'lineCount' | 'children'
> & {
  wrap?: boolean;
  copyToClipboardSupport?: 'always' | 'overflow';
  tooltipMaxWidth?: TooltipProps['maxWidth'];
  tooltipPlacement?: TooltipProps['placement'];
  tooltipAlign?: TooltipProps['align'];
};

const SIZE_DIFFERENCE_THRESHOLD = 1;
const interactiveElementSelector = [linkStyles.root].map((className) => `.${className}`).join(', ');

export const OverflowingTextTip: FC<OverflowingTextTipProps> = (props) => {
  const {
    className,
    lineCount,
    children,
    wrap = typeof lineCount !== 'undefined',
    copyToClipboardSupport,
    tooltipMaxWidth = 500,
    tooltipPlacement,
    tooltipAlign = tooltipPlacement === undefined || tooltipPlacement === 'bottom'
      ? props.align === 'left'
        ? 'start'
        : props.align === 'right'
          ? 'end'
          : props.align
      : undefined,
    ...textProps
  } = props;

  const rootRef = useRef<HTMLDivElement | null>(null);

  const {
    ref: rootSizeRef,
    size: { width: rootWidth, height: rootHeight },
  } = useElementSize();

  const rootCombinedRef = useCombineRefs([rootRef, rootSizeRef]);
  const isTouch = useIsTouch();
  const copyToClipboard = useCopyToClipboard();

  const isOverflowing = useMemo(() => {
    return (
      rootRef.current &&
      (rootRef.current.scrollWidth - rootWidth > SIZE_DIFFERENCE_THRESHOLD ||
        rootRef.current.scrollHeight - rootHeight > SIZE_DIFFERENCE_THRESHOLD)
    );
  }, [rootHeight, rootWidth]);

  const textToCopy = useMemo(() => {
    return getNodeText(children);
  }, [children]);

  const withCopyToClipboardSupport =
    copyToClipboardSupport === 'always' || (copyToClipboardSupport === 'overflow' && isOverflowing);

  const handleTextClick = useCallback(
    (e: MouseEvent) => {
      if (
        !withCopyToClipboardSupport ||
        (e.target instanceof Element && e.target.closest(interactiveElementSelector))
      ) {
        return;
      }

      copyToClipboard(textToCopy);
    },
    [copyToClipboard, textToCopy, withCopyToClipboardSupport]
  );

  const node = useMemo(() => {
    return (
      <Text
        className={clsx(
          styles.text,
          {
            [styles.nowrap]: !wrap,
            [styles.withCopyToClipboardSupport]: withCopyToClipboardSupport,
          },
          className
        )}
        ref={rootCombinedRef}
        lineCount={lineCount}
        ellipsis
        onClick={handleTextClick}
        {...textProps}
      >
        {children}
      </Text>
    );
  }, [children, className, handleTextClick, lineCount, rootCombinedRef, textProps, withCopyToClipboardSupport, wrap]);

  const popup = useMemo(() => {
    return (
      <span onClick={isTouch && copyToClipboardSupport ? () => copyToClipboard(textToCopy) : undefined}>
        {textToCopy}
      </span>
    );
  }, [copyToClipboard, copyToClipboardSupport, isTouch, textToCopy]);

  return isOverflowing ? (
    <Tooltip
      popupClassName={styles.popup}
      popup={popup}
      placement={tooltipPlacement}
      align={tooltipAlign}
      maxWidth={tooltipMaxWidth}
    >
      {node}
    </Tooltip>
  ) : (
    node
  );
};
